<template>
  <div v-rect-loading="loading" class="interview-meeting-room-editor-page">
    <div class="editor-header">
      <div class="editor-title">{{ titleLabel }}</div>
    </div>
    <div class="form-container">
      <WrappedFrom
        ref="form"
        class="user-management-info"
        label-width="220px"
        :model="interviewMeetingRoomData"
        :formLines="formLines"
      >
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ cancelBtnLabel }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ saveBtnLabel }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { selectLabelI18n } from '@/i18n'
import { mapActions } from 'vuex'
import { mixedRouterPush } from '@/router/routes'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { validateEmail } from '@/utils/validator'

export default {
  components: {
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'admin.company.interviewMeetingRoomManagement',
      loading: false,
      loadFirst: true,
      interviewMeetingRoomData: {
        cn_name: null,
        name: null,
        address: null,
        email: null,
        note: null
      }
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    interviewMeetingRoomId () {
      return this.$route.params.interviewMeetingRoomId
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}.title.${this.isEditMode ? 'editInterviewMeetingRoom' : 'addInterviewMeetingRoom'}`)
    },
    cancelBtnLabel () {
      return selectLabelI18n(this, 'cancelBtn', 'common.btn.cancel')
    },
    saveBtnLabel () {
      return selectLabelI18n(this, 'saveBtn', 'common.btn.save')
    },
    formLines () {
      const lines = {
        cn_name: {
          label: this.$t(`${this.i18nPrefix}.label.cnName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.cnName`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        name: {
          label: this.$t(`${this.i18nPrefix}.label.name`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.name`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        address: {
          label: this.$t(`${this.i18nPrefix}.label.address`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.address`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        email: {
          label: this.$t(`${this.i18nPrefix}.label.email`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.email`),
          rules: [
            { validator: this.validateEmail, trigger: 'blur' }
          ]
        },
        note: {
          label: this.$t(`${this.i18nPrefix}.label.note`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.note`),
          props: { type: 'textarea', autosize: { minRows: 3 } }
        }
      }
      return lines
    },
    form () {
      const form = this.$refs.form
      return form
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('interviewMeetingRoom', ['getInterviewMeetingRoomById', 'createInterviewMeetingRoom', 'updateInterviewMeetingRoom']),
    async initData () {
      if (!this.isEditMode) {
        return
      }
      this.loading = true
      try {
        const resp = await this.getInterviewMeetingRoomById({ id: this.interviewMeetingRoomId })
        this.interviewMeetingRoomData = resp
      } finally {
        this.loading = false
      }
    },
    async submit () {
      let resp
      if (this.isEditMode) {
        resp = await this.updateInterviewMeetingRoom({ id: this.interviewMeetingRoomId, data: this.interviewMeetingRoomData })
      } else {
        resp = await this.createInterviewMeetingRoom(this.interviewMeetingRoomData)
      }
      if (resp) {
        this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
        this.$router.replace({ path: '/v-admin/interview-meeting-room/management/list', query: this.$route.query })
      } else {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.push({ path: '/v-admin/interview-meeting-room/management/list', query: this.$route.query })
      }
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        let result = false
        result = await this.form.submit()
        if (!result) {
          return
        }
        await this.submit()
      } finally {
        this.loading = false
      }
    },
    validateEmail (rule, value, callback) {
      if (!value) {
        return callback()
      }
      return validateEmail(rule, value, callback)
    }
  }
}
</script>

<style lang="scss" scoped>
.interview-meeting-room-editor-page {
  .editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .form-container {
    width: 606px;
    margin: 30px auto 0;
  }

  .bottom-btn-group {
    display: flex;
    justify-content: flex-end;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
